import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import { Fragment, ReactNode, useRef, useState } from "react";

import { Icon } from "../Icon";
import type { Icons } from "../Icon/utils";

export type MultiSelectOption = {
  description?: string;
  disabled?: boolean;
  icon?: keyof typeof Icons;
  label: ReactNode;
  value: string;
};

type SearchWithMultiSelectProps = {
  disabled?: boolean;
  dropdownPlacement?: "bottom" | "top";
  hideClearButton?: boolean;
  invalid?: boolean;
  maxVisibleChips?: number;
  name?: string;
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  selected?: string[];
  showChips?: boolean;
  showSearchIcon?: boolean;
};

export default function SearchWithMultiSelect({
  disabled,
  dropdownPlacement = "bottom",
  hideClearButton,
  invalid,
  maxVisibleChips = 1,
  name,
  onChange,
  options,
  placeholder = "Search...",
  selected = [],
  showChips,
  showSearchIcon,
}: SearchWithMultiSelectProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const visibleChips = showChips ? selected.slice(0, maxVisibleChips) : [];
  const overflowCount = showChips ? selected.length - visibleChips.length : 0;
  const labelForValue = (value: string) => {
    const opt = options.find((option) => option.value === value);
    return typeof opt?.label === "string" ? opt.label : value;
  };
  const removeValue = (e: React.MouseEvent, value: string) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  const filteredOptions =
    query === ""
      ? options
      : options.filter((opt) => {
          const label =
            typeof opt.label === "string" ? opt.label : String(opt.label);
          return label.toLowerCase().includes(query.toLowerCase());
        });

  const handleSelect = (values: string[]) => {
    onChange(values);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setQuery("");
  };

  return (
    <div className="relative w-full">
      <Combobox
        value={selected}
        onChange={handleSelect}
        disabled={disabled}
        name={name}
        data-testid={`search-multi-select-${name}`}
        multiple
        immediate
      >
        {({ open }) => (
          <div className="w-full">
            <div
              onClick={() => !disabled && inputRef.current?.focus()}
              className={clsx(
                "flex w-full cursor-text items-center gap-1 rounded-md border px-3 py-2 shadow-sm bg-white",
                "text-xs lg:text-sm",
                invalid
                  ? "border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                  : "border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500",
                disabled && "cursor-not-allowed bg-gray-50",
                "hover:border-gray-400 transition-colors duration-200",
              )}
            >
              <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                {showSearchIcon && (
                  <Icon
                    name="magnifyingGlass"
                    className="h-3 w-3 text-gray-400 flex-shrink-0"
                  />
                )}
                {visibleChips.map((value) => (
                  <span
                    key={value}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-900"
                  >
                    <span className="max-w-[88px] truncate">
                      {labelForValue(value)}
                    </span>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => removeValue(e, value)}
                        aria-label={`Remove ${labelForValue(value)}`}
                      >
                        <Icon
                          name="xMark"
                          className="h-3 w-3 text-gray-500 hover:text-gray-700"
                        />
                      </button>
                    )}
                  </span>
                ))}
                <ComboboxInput
                  ref={inputRef}
                  className={clsx(
                    "min-w-0 flex-1 border-none p-0 focus:outline-none focus:ring-0",
                    "text-xs lg:text-sm",
                    disabled
                      ? "cursor-not-allowed bg-gray-50 text-gray-400"
                      : "text-gray-900 ",
                  )}
                  placeholder={placeholder}
                  onChange={(e) => setQuery(e.target.value)}
                  displayValue={() => query}
                  autoComplete="off"
                />
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                {overflowCount > 0 && (
                  <span className="inline-flex flex-shrink-0 items-center rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-900">
                    +{overflowCount}
                  </span>
                )}
                {selected.length > 0 &&
                  !disabled &&
                  !hideClearButton &&
                  !showChips && (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="px-1 rounded-sm hover:bg-gray-100 transition-colors duration-200"
                      title="Clear all"
                    >
                      <Icon
                        name="xMark"
                        className={clsx(
                          "text-gray-400 hover:text-gray-600",
                          "h-3 w-3",
                        )}
                      />
                    </button>
                  )}
                {showChips && (
                  <Icon
                    name="chevronDown"
                    className={clsx(
                      "h-3 w-3 text-gray-400 transition-all duration-200",
                      open && "rotate-180",
                    )}
                  />
                )}
              </div>
            </div>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ComboboxOptions
                static
                className={clsx(
                  "absolute z-[60] left-0 right-0 rounded-md max-h-56 overflow-auto bg-white py-1",
                  "text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                  "mb-2 mt-2 px-2",
                  dropdownPlacement === "top"
                    ? "mb-2 bottom-full"
                    : "mt-2 top-full",
                )}
              >
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    No results found
                  </div>
                ) : (
                  filteredOptions.map((opt, idx) => {
                    const isSelected = selected.includes(opt.value);
                    return (
                      <ComboboxOption
                        key={opt.value}
                        value={opt.value}
                        data-testid={`${opt.value}-${idx}`}
                        disabled={opt.disabled}
                        className={({ selected }) =>
                          clsx(
                            "relative cursor-pointer select-none px-3 py-2 pr-10 flex justify-between items-center w-full",
                            selected ? "bg-gray-100" : "text-gray-900",
                            opt.disabled && "cursor-not-allowed opacity-50",
                            "data-selected:!bg-blue-600 data-selected:text-white",
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <div
                              className={clsx(
                                "flex items-center",
                                opt.disabled && "text-gray-400",
                                isSelected ? "w-[calc(90%)]" : "w-[calc(100%)]",
                              )}
                            >
                              {opt.icon && (
                                <Icon
                                  name={opt.icon}
                                  className={clsx(
                                    "mr-2",
                                    "h-3 lg:h-4 w-3 lg:w-4",
                                    selected
                                      ? "text-gray-900"
                                      : "text-gray-600",
                                  )}
                                />
                              )}
                              <div className="flex flex-col w-full">
                                <span
                                  className={clsx(
                                    "block truncate text-ellipsis w-[calc(100%)] text-xs lg:text-sm",
                                    isSelected ? "font-medium" : "font-normal",
                                  )}
                                >
                                  {opt.label}
                                </span>
                                {opt.description && (
                                  <span className="text-[10px] lg:text-xs text-gray-500 truncate">
                                    {opt.description}
                                  </span>
                                )}
                              </div>
                            </div>

                            {isSelected ? (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 right-0 flex items-center justify-end pr-3 w-[calc(10%)]",
                                  selected ? "text-gray-900" : "text-gray-600",
                                )}
                              >
                                <Icon name="fasTrash" className="h-3 w-3" />
                              </span>
                            ) : (
                              <span
                                className={clsx(
                                  "absolute inset-y-0 right-0 flex items-center justify-end pr-3 w-[calc(10%)]",
                                  selected ? "text-gray-900" : "text-gray-600",
                                )}
                              >
                                <Icon name="fasPlus" className="h-3 w-3" />
                              </span>
                            )}
                          </>
                        )}
                      </ComboboxOption>
                    );
                  })
                )}
              </ComboboxOptions>
            </Transition>
          </div>
        )}
      </Combobox>
    </div>
  );
}
